import type {
  PaginatedList,
  PaginationData,
  Patrol,
  Person,
} from '@local/common-lib';
import { Edit as EditIcon } from '@mui/icons-material';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  Grid,
  Pagination,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmButton } from '../../components/confirm-button/index.js';
import { DisplayField } from '../../components/display-field/index.js';
import { CenterLayout } from '../../components/page-layout/index.js';
import { useAuthenticated } from '../../utils/auth/index.js';
import { roles } from '../../utils/roles.js';
import { NarrowPatrolList } from '../patrol-list/narrow-view.js';
import { PersonPatrolsSummary } from '../patrol-list/summary.js';
import { WidePatrolList } from '../patrol-list/wide-view.js';

interface ViewProps<T> {
  busy: boolean;
  data: T;
  edit: () => void;
  handleFinish: () => void;
  handleResume: () => void;
  hideUnstartedBadge?: boolean;
  id: string;
  recentPatrolPagination: PaginationData;
  recentPatrols: PaginatedList<Patrol.Type>;
  scheduledPatrols: PaginatedList<Patrol.Type>;
  setRecentPatrolPagination: (pagination: PaginationData) => void;
}

export const DisplayPersonView: FC<ViewProps<Person.View>> = ({
  scheduledPatrols,
  busy,
  data,
  edit,
  handleFinish,
  handleResume,
  hideUnstartedBadge,
  recentPatrols,
  recentPatrolPagination,
  setRecentPatrolPagination,
}) => {
  const auth = useAuthenticated();
  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const limited = !auth.userInfo.hasRole(
    'admin',
    'watcher',
    'summary-recipient',
  );
  const handleSelection = (id: string): unknown =>
    !limited && navigate(`/patrol/${id}`);

  return (
    <CenterLayout>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <PersonPatrolsSummary personId={data._id} />
          </Card>
        </Grid>
        <Grid item md={4}>
          <Card>
            {auth.userInfo.hasRole('admin') && (
              <CardActions sx={{ justifyContent: 'end' }}>
                <Stack direction="row" spacing={1}>
                  {data.finished ? (
                    <ConfirmButton
                      busy={busy}
                      onClick={handleResume}
                      variant="text"
                    >
                      Resume
                    </ConfirmButton>
                  ) : (
                    <ConfirmButton
                      busy={busy}
                      onClick={handleFinish}
                      variant="text"
                    >
                      Finish
                    </ConfirmButton>
                  )}
                  <Button
                    onClick={edit}
                    startIcon={<EditIcon />}
                    variant="outlined"
                  >
                    Edit
                  </Button>
                </Stack>
              </CardActions>
            )}
            <CardContent>
              <Grid container spacing={3}>
                {data.finished && (
                  <Grid item xs={12}>
                    <Chip
                      label="Finished"
                      size="small"
                      sx={{ backgroundColor: 'red' }}
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <DisplayField
                    label="Name"
                    value={`${data.firstName} ${data.lastName.toUpperCase()}`}
                  />
                </Grid>
                <Grid item xs={12}>
                  <DisplayField label="Email address" value={data.email} />
                </Grid>
                <Grid item xs={12}>
                  <DisplayField label="Phone" value={data.phone} />
                </Grid>
                <Grid item xs={12}>
                  <DisplayField
                    label="Roles"
                    value={
                      data.roles.length ? (
                        data.roles
                          .sort()
                          .map((role) => (
                            <Chip
                              key={role}
                              label={roles[role] || role}
                              sx={{ mb: 1, mr: 1 }}
                            />
                          ))
                      ) : (
                        <Typography
                          sx={{ color: 'GrayText', fontStyle: 'italic' }}
                        >
                          No roles assigned
                        </Typography>
                      )
                    }
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        <Grid item md={8} sx={{ flexGrow: 1 }}>
          <Stack direction="column" spacing={3}>
            <Card>
              <CardHeader title="Scheduled Patrols" />
              {scheduledPatrols.matches ? (
                smallScreen ? (
                  <NarrowPatrolList
                    handleSelection={handleSelection}
                    hideWho={limited}
                    patrols={scheduledPatrols}
                  />
                ) : (
                  <WidePatrolList
                    handleSelection={handleSelection}
                    hideWho={limited}
                    patrols={scheduledPatrols}
                  />
                )
              ) : (
                <CardContent>
                  <Typography sx={{ color: 'GrayText', fontStyle: 'italic' }}>
                    No future patrols scheduled
                  </Typography>
                </CardContent>
              )}
            </Card>
            <Card>
              <CardHeader title="Recent Patrols" />
              {recentPatrols.matches ? (
                smallScreen ? (
                  <NarrowPatrolList
                    handleSelection={handleSelection}
                    hideUnstartedBadge={hideUnstartedBadge}
                    hideWho={limited}
                    patrols={recentPatrols}
                  />
                ) : (
                  <WidePatrolList
                    handleSelection={handleSelection}
                    hideUnstartedBadge={hideUnstartedBadge}
                    hideWho={limited}
                    patrols={recentPatrols}
                  />
                )
              ) : (
                <CardContent>
                  <Typography sx={{ color: 'GrayText', fontStyle: 'italic' }}>
                    No past patrols found
                  </Typography>
                </CardContent>
              )}

              {recentPatrols.matches > recentPatrolPagination.limit ? (
                <Pagination
                  count={Math.ceil(
                    recentPatrols.matches / recentPatrolPagination.limit,
                  )}
                  onChange={(_, page) => {
                    setRecentPatrolPagination({
                      limit: recentPatrolPagination.limit,
                      skip: recentPatrolPagination.limit * (page - 1),
                    });
                  }}
                  page={
                    Math.floor(
                      recentPatrolPagination.skip /
                        recentPatrolPagination.limit,
                    ) + 1
                  }
                  showFirstButton
                  showLastButton
                  sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}
                />
              ) : undefined}
            </Card>
          </Stack>
        </Grid>
      </Grid>
    </CenterLayout>
  );
};
